import { useEffect, useState, useRef } from "react";
import {
  Button,
  Table,
  Select,
  Checkbox,
  Card,
  Tag,
  Radio,
  Modal,
  Form,
  InputNumber,
  Input,
  DatePicker,
  Popconfirm,
} from "antd";
import { useSearchParams } from "react-router-dom";
import useTransfer from "../hooks/useTransfer";
import usePassengers from "../hooks/usePassengers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Wallet, Download, Plus, Minus, Trash2 } from "lucide-react";
import * as XLSX from "xlsx";
import useParseString from "../hooks/useParseString";
import DebtModal from "../components/transfers/DebtModal";
import PassengerModal from "../components/PassengerModal";
import PageDocumentation from "../components/PageDocumentation";

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

// Transaction types from backend
const TRANSACTION_TYPES = [
  { value: "RENT", label: "Alquiler" },
  { value: "SALARY", label: "Sueldo" },
  { value: "PROFIT_WITHDRAWAL", label: "Retiro de Ganancias" },
  { value: "WATER", label: "Agua" },
  { value: "ELECTRICITY", label: "Electricidad" },
  { value: "GAS", label: "Gas" },
  { value: "EXPENSES", label: "Servicios" },
  { value: "OTHER", label: "Otros" },
];

// Generate year options (last 5 years)
const currentYear = dayjs().year();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => ({
  value: String(currentYear - i),
  label: String(currentYear - i),
}));

// Month options
const MONTH_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "1", label: "Enero" },
  { value: "2", label: "Febrero" },
  { value: "3", label: "Marzo" },
  { value: "4", label: "Abril" },
  { value: "5", label: "Mayo" },
  { value: "6", label: "Junio" },
  { value: "7", label: "Julio" },
  { value: "8", label: "Agosto" },
  { value: "9", label: "Septiembre" },
  { value: "10", label: "Octubre" },
  { value: "11", label: "Noviembre" },
  { value: "12", label: "Diciembre" },
];

// Day options (1-31)
const DAY_OPTIONS = [
  { value: "", label: "Todos" },
  ...Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  })),
];

function Caja() {
  const [searchParams] = useSearchParams();
  const { formatCurrency } = useParseString();
  const {
    transfersByBox,
    getTransfersByBox,
    createTransaction,
    getTransaction,
    updateTransaction,
    deleteTransaction,
    loading: transferLoading,
  } = useTransfer();
  const { paymentMethods, getPaymentMethods } = usePassengers();

  const [form] = Form.useForm();
  // Modal para crear transacciones (ingreso/egreso)
  const [transactionModal, setTransactionModal] = useState({
    open: false,
    boxId: null,
    isIncome: true, // true = ingreso, false = egreso
  });
  // Modal para editar transacciones existentes
  const [editModal, setEditModal] = useState({
    open: false,
    transaction: null,
  });
  const [debtModal, setDebtModal] = useState({
    open: false,
    userId: null,
    type: null,
    extraInfo: null,
    transferId: null
  });
  const [passengerModal, setPassengerModal] = useState({
    open: false,
    passengerId: null
  });

  // Leer params de URL o usar valores por defecto
  const [params, setParams] = useState({
    year: searchParams.get("year") || String(currentYear),
    month: searchParams.get("month") || String(dayjs().month() + 1),
    day: searchParams.get("day") || "",
    type: [], // Array para multiselect
    include_passenger_payments: true,
  });

  const [selectedBox, setSelectedBox] = useState("all"); // "all" = todos
  const tableContainerRef = useRef(null);

  // Filter boxes based on selectedBox - debe definirse antes de los useEffect que lo usan
  const filteredBoxes = selectedBox === "all"
    ? transfersByBox.boxes
    : transfersByBox.boxes?.filter(box => String(box.boxId || "sin-caja") === selectedBox);

  useEffect(() => {
    getPaymentMethods();
  }, []);

  useEffect(() => {
    const queryParams = {};
    if (params.year) queryParams.year = params.year;
    if (params.month) queryParams.month = params.month;
    if (params.day) queryParams.day = params.day;
    // Enviar tipos como string separado por comas
    if (params.type && params.type.length > 0) {
      queryParams.type = params.type.join(",");
    }
    queryParams.include_passenger_payments = params.include_passenger_payments;

    getTransfersByBox(queryParams);
  }, [params]);

  // Auto-seleccionar método de pago cuando se abre el modal de crear transacción
  useEffect(() => {
    if (transactionModal.open && transactionModal.boxId) {
      // Buscar el método de pago que corresponde a la caja
      const matchingMethod = paymentMethods.find(
        (pm) => pm.boxId === transactionModal.boxId
      );
      if (matchingMethod) {
        form.setFieldValue("paymentMethodId", matchingMethod.id);
      }
      // Reset fecha al abrir (UTC-3 Argentina)
      form.setFieldValue("createdAt", dayjs().tz("America/Argentina/Buenos_Aires"));
    }
  }, [transactionModal.open, transactionModal.boxId, paymentMethods, form]);

  // Setear valores del form cuando se abre el modal de edición
  useEffect(() => {
    if (editModal.open && editModal.transaction) {
      form.setFieldsValue({
        type: editModal.transaction.type,
        amount: Math.abs(editModal.transaction.amount),
        paymentMethodId: editModal.transaction.paymentMethodId,
        description: editModal.transaction.description,
        createdAt: editModal.transaction.createdAt
          ? dayjs(editModal.transaction.createdAt)
          : null,
      });
    }
  }, [editModal.open, editModal.transaction, form]);

  // Scroll al fondo de las tablas cuando se cargan los datos
  useEffect(() => {
    if (!transferLoading.get && filteredBoxes?.length > 0) {
      // Pequeño delay para asegurar que la tabla se renderizó
      setTimeout(() => {
        const tableContainers = document.querySelectorAll('.ant-table-body');
        tableContainers.forEach(container => {
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        });
      }, 100);
    }
  }, [transferLoading.get, filteredBoxes, selectedBox]);

  const handleFilterChange = (key, value) => {
    setParams((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleClearFilters = () => {
    setParams({
      year: String(currentYear),
      month: String(dayjs().month() + 1),
      day: "",
      type: [], // Array vacío para multiselect
      include_passenger_payments: true,
    });
  };

  const handleRowClick = (record) => {
    const detailUrl = record.detailUrl;

    // Si es una transacción (gasto/ingreso extraordinario), abrir modal de edición
    if (record.source === "transaction") {
      handleEditTransaction(record.id);
      return;
    }

    if (!detailUrl) return;

    // Parsear URL: /pasajeros/:passengerId
    const passengerMatch = detailUrl.match(/\/pasajeros\/(\d+)/);
    // Parsear URL: /transfers/debt/:userId?type=X&extraInfo=Y
    const debtMatch = detailUrl.match(/\/transfers\/debt\/(\d+)\?type=([^&]+)&extraInfo=(.+)/);
    // o: /transfers/:transferId/detail
    const transferMatch = detailUrl.match(/\/transfers\/(\d+)\/detail/);

    if (passengerMatch) {
      setPassengerModal({
        open: true,
        passengerId: passengerMatch[1]
      });
    } else if (debtMatch) {
      setDebtModal({
        open: true,
        userId: debtMatch[1],
        type: debtMatch[2],
        extraInfo: debtMatch[3],
        transferId: null
      });
    } else if (transferMatch) {
      setDebtModal({
        open: true,
        userId: null,
        type: null,
        extraInfo: null,
        transferId: transferMatch[1]
      });
    }
  };

  // Abrir modal de edición de transacción
  const handleEditTransaction = async (transactionId) => {
    const data = await getTransaction(transactionId);
    if (data && data.id) {
      setEditModal({
        open: true,
        transaction: data,
      });
    }
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setEditModal({ open: false, transaction: null });
    form.resetFields();
  };

  // Guardar cambios de transacción
  const handleSaveTransaction = async (values) => {
    const transactionId = editModal.transaction?.id;
    if (!transactionId) return;

    // Determinar si es ingreso o egreso basándose en el monto original
    const originalAmount = editModal.transaction?.amount || 0;
    const isIncome = originalAmount >= 0;
    const amount = isIncome ? Math.abs(values.amount) : -Math.abs(values.amount);

    const result = await updateTransaction(transactionId, {
      type: values.type,
      amount: amount,
      paymentMethodId: values.paymentMethodId,
      description: values.description || "",
      createdAt: values.createdAt?.toISOString(),
    });

    if (result?.id) {
      handleCloseEditModal();
      refreshData();
    }
  };

  // Eliminar transacción
  const handleDeleteTransaction = async () => {
    const transactionId = editModal.transaction?.id;
    if (!transactionId) return;

    const result = await deleteTransaction(transactionId);
    if (result?.message) {
      handleCloseEditModal();
      refreshData();
    }
  };

  const refreshData = () => {
    const queryParams = {};
    if (params.year) queryParams.year = params.year;
    if (params.month) queryParams.month = params.month;
    if (params.day) queryParams.day = params.day;
    if (params.type && params.type.length > 0) {
      queryParams.type = params.type.join(",");
    }
    queryParams.include_passenger_payments = params.include_passenger_payments;
    getTransfersByBox(queryParams);
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Create one sheet per box
    filteredBoxes?.forEach((box) => {
      const transactions = [];

      box.transfers?.forEach((transfer) => {
        transactions.push({
          Fecha: transfer.dateFormatted,
          Remitente: transfer.sender,
          Receptor: transfer.receiver,
          Tipo: transfer.tag,
          Descripción: transfer.description,
          Ingresos: transfer.income || 0,
          Egresos: transfer.expense || 0,
          Saldo: transfer.balance || 0,
        });
      });

      // Add totals row
      transactions.push({
        Fecha: "TOTALES",
        Remitente: "",
        Receptor: "",
        Tipo: "",
        Descripción: "",
        Ingresos: box.totals?.income || 0,
        Egresos: box.totals?.expense || 0,
        Saldo: box.totals?.balance || 0,
      });

      const ws = XLSX.utils.json_to_sheet(transactions);

      // Set column widths
      ws["!cols"] = [
        { wch: 18 },  // Fecha
        { wch: 20 },  // Remitente
        { wch: 20 },  // Receptor
        { wch: 15 },  // Tipo
        { wch: 40 },  // Descripción
        { wch: 12 },  // Ingresos
        { wch: 12 },  // Egresos
        { wch: 12 },  // Saldo
      ];

      // Sanitize sheet name (max 31 chars, no special chars)
      const sheetName = (box.boxName || "Sin Caja").slice(0, 31).replace(/[:\\/?*[\]]/g, "");
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    // Generate filename with date filters
    const monthName = params.month ? MONTH_OPTIONS.find(m => m.value === params.month)?.label : "Todos";
    const fileName = `Caja_${params.year}_${monthName}${params.day ? `_${params.day}` : ""}.xlsx`;

    // Download file
    XLSX.writeFile(wb, fileName);
  };

  const columns = [
    {
      title: "Fecha",
      dataIndex: "dateFormatted",
      key: "date",
      width: 100,
    },
    {
      title: "Remitente",
      dataIndex: "sender",
      key: "sender",
      width: 110,
      ellipsis: true,
    },
    {
      title: "Receptor",
      dataIndex: "receiver",
      key: "receiver",
      width: 110,
      ellipsis: true,
    },
    {
      title: "Descripción",
      dataIndex: "description",
      key: "description",
      render: (text, record) => (
        <div className="flex items-start gap-2">
          <Tag color={record.tagColor} style={{ margin: 0, flexShrink: 0 }}>
            {record.tag}
          </Tag>
          <span className="whitespace-normal break-words">{text}</span>
        </div>
      ),
    },
    {
      title: "Ingresos",
      dataIndex: "income",
      key: "income",
      width: 120,
      align: "right",
      render: (value) =>
        value > 0 ? (
          <span className="text-green-600">{formatCurrency(value)}</span>
        ) : null,
    },
    {
      title: "Egresos",
      dataIndex: "expense",
      key: "expense",
      width: 120,
      align: "right",
      render: (value) =>
        value > 0 ? (
          <span className="text-red-500">{formatCurrency(value)}</span>
        ) : null,
    },
    {
      title: "Saldo",
      dataIndex: "balance",
      key: "balance",
      width: 120,
      align: "right",
      render: (value) => (
        <span
          className={`font-medium ${
            value >= 0 ? "text-green-600" : "text-red-500"
          }`}
        >
          {formatCurrency(value)}
        </span>
      ),
    },
  ];

  // Build type options from API response (sin opción "Todos" para multiselect)
  const typeOptions = (transfersByBox.transferTypes || []).map((type) => ({
    value: type,
    label: type,
  }));

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet size={24} className="text-blue-600" />
          <h1 className="text-xl font-semibold m-0">Transferencias por Caja</h1>
        </div>
        <PageDocumentation docKey="caja" />
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <span className="block text-sm text-gray-500 mb-1">Año</span>
            <Select
              value={params.year}
              onChange={(value) => handleFilterChange("year", value)}
              options={YEAR_OPTIONS}
              style={{ width: 100 }}
            />
          </div>

          <div>
            <span className="block text-sm text-gray-500 mb-1">Mes</span>
            <Select
              value={params.month}
              onChange={(value) => handleFilterChange("month", value)}
              options={MONTH_OPTIONS}
              style={{ width: 130 }}
            />
          </div>

          <div>
            <span className="block text-sm text-gray-500 mb-1">Día</span>
            <Select
              value={params.day}
              onChange={(value) => handleFilterChange("day", value)}
              options={DAY_OPTIONS}
              style={{ width: 90 }}
            />
          </div>

          <div>
            <span className="block text-sm text-gray-500 mb-1">Tipo</span>
            <Select
              mode="multiple"
              value={params.type}
              onChange={(value) => handleFilterChange("type", value)}
              options={typeOptions}
              style={{ minWidth: 200 }}
              placeholder="Todos los tipos"
              allowClear
              maxTagCount="responsive"
            />
          </div>

          <div className="flex items-center h-[32px]">
            <Checkbox
              checked={params.include_passenger_payments}
              onChange={(e) =>
                handleFilterChange("include_passenger_payments", e.target.checked)
              }
            >
              Incluir Pagos de Pasajeros
            </Checkbox>
          </div>

          <Button onClick={handleClearFilters}>Limpiar</Button>

          <Button
            icon={<Download size={16} />}
            onClick={exportToExcel}
            disabled={!filteredBoxes?.length}
          >
            Exportar Excel
          </Button>
        </div>
      </Card>

      {/* Modal para crear transacción (ingreso/egreso) */}
      <Modal
        title={transactionModal.isIncome ? "Nuevo Ingreso" : "Nuevo Egreso"}
        open={transactionModal.open}
        onCancel={() => {
          setTransactionModal({ open: false, boxId: null, isIncome: true });
          form.resetFields();
        }}
        onOk={() => form.submit()}
        confirmLoading={transferLoading.post}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (values) => {
            // El monto siempre es positivo en el form, aplicamos el signo según el tipo
            const amount = transactionModal.isIncome
              ? Math.abs(values.amount)
              : -Math.abs(values.amount);

            const result = await createTransaction({
              type: values.type,
              amount: amount,
              paymentMethodId: values.paymentMethodId,
              description: values.description || "",
              createdAt: values.createdAt?.toISOString(),
            });
            if (result?.id) {
              setTransactionModal({ open: false, boxId: null, isIncome: true });
              form.resetFields();
              refreshData();
            }
          }}
        >
          <Form.Item
            name="createdAt"
            label="Fecha y Hora"
            rules={[{ required: true, message: "Selecciona fecha y hora" }]}
          >
            <DatePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            name="type"
            label="Tipo"
            rules={[{ required: true, message: "Selecciona un tipo" }]}
          >
            <Select
              placeholder="Seleccionar tipo"
              options={TRANSACTION_TYPES}
            />
          </Form.Item>

          <Form.Item
            name="amount"
            label={`Monto (${transactionModal.isIncome ? "Ingreso" : "Egreso"})`}
            rules={[{ required: true, message: "Ingresa el monto" }]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={0}
              placeholder="Ej: 5000"
              prefix={transactionModal.isIncome ? "+" : "-"}
              formatter={(value) =>
                `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>

          <Form.Item
            name="paymentMethodId"
            label="Método de Pago"
            rules={[{ required: true, message: "Selecciona un método de pago" }]}
          >
            <Select
              placeholder="Seleccionar método de pago"
              options={paymentMethods.map((pm) => ({
                value: pm.id,
                label: pm.name,
              }))}
            />
          </Form.Item>

          <Form.Item name="description" label="Descripción">
            <Input.TextArea placeholder="Descripción opcional" rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal para editar transacción existente */}
      <Modal
        title={
          editModal.transaction?.amount >= 0
            ? "Editar Ingreso"
            : "Editar Egreso"
        }
        open={editModal.open}
        onCancel={handleCloseEditModal}
        footer={[
          <Popconfirm
            key="delete"
            title="¿Eliminar transacción?"
            description="Esta acción no se puede deshacer"
            onConfirm={handleDeleteTransaction}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<Trash2 size={16} />}
              loading={transferLoading.delete}
            >
              Eliminar
            </Button>
          </Popconfirm>,
          <Button key="cancel" onClick={handleCloseEditModal}>
            Cancelar
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={() => form.submit()}
            loading={transferLoading.put}
          >
            Guardar
          </Button>,
        ]}
      >
        {editModal.transaction && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSaveTransaction}
          >
            <Form.Item
              name="createdAt"
              label="Fecha y Hora"
              rules={[{ required: true, message: "Selecciona fecha y hora" }]}
            >
              <DatePicker
                showTime
                format="DD/MM/YYYY HH:mm"
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item
              name="type"
              label="Tipo"
              rules={[{ required: true, message: "Selecciona un tipo" }]}
            >
              <Select
                placeholder="Seleccionar tipo"
                options={TRANSACTION_TYPES}
              />
            </Form.Item>

            <Form.Item
              name="amount"
              label={`Monto (${
                editModal.transaction?.amount >= 0 ? "Ingreso" : "Egreso"
              })`}
              rules={[{ required: true, message: "Ingresa el monto" }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                placeholder="Ej: 5000"
                prefix={editModal.transaction?.amount >= 0 ? "+" : "-"}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>

            <Form.Item
              name="paymentMethodId"
              label="Método de Pago"
              rules={[
                { required: true, message: "Selecciona un método de pago" },
              ]}
            >
              <Select
                placeholder="Seleccionar método de pago"
                options={paymentMethods.map((pm) => ({
                  value: pm.id,
                  label: pm.name,
                }))}
              />
            </Form.Item>

            <Form.Item name="description" label="Descripción">
              <Input.TextArea placeholder="Descripción opcional" rows={2} />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Box Filter Buttons */}
      <div className="mb-4">
        <Radio.Group
          value={selectedBox}
          onChange={(e) => setSelectedBox(e.target.value)}
          optionType="button"
          buttonStyle="solid"
        >
          <Radio.Button value="all">Todos</Radio.Button>
          {transfersByBox.boxes?.map((box, index) => (
            <Radio.Button key={box.boxId || `sin-caja-${index}`} value={String(box.boxId || "sin-caja")}>
              {box.boxName}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      {/* Tables by Box */}
      <div className="flex flex-col gap-4">
        {filteredBoxes?.map((box, index) => (
          <Card
            key={box.boxId || `sin-caja-${index}`}
            title={box.boxName}
            extra={
              <div className="flex gap-2">
                <Button
                  type="primary"
                  size="small"
                  icon={<Plus size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTransactionModal({
                      open: true,
                      boxId: box.boxId,
                      isIncome: true,
                    });
                  }}
                >
                  Ingreso
                </Button>
                <Button
                  danger
                  size="small"
                  icon={<Minus size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTransactionModal({
                      open: true,
                      boxId: box.boxId,
                      isIncome: false,
                    });
                  }}
                >
                  Egreso
                </Button>
              </div>
            }
            styles={{ body: { padding: 0 } }}
          >
            <Table
              dataSource={box.transfers}
              columns={columns}
              size="small"
              rowKey={(record) => `${record.source}-${record.id}`}
              loading={transferLoading.get}
              pagination={false}
              scroll={{ y: selectedBox === "all" ? "calc(95vh - 350px)" : 500 }}
              onRow={(record) => ({
                onClick: () => handleRowClick(record),
                style: { cursor: record.detailUrl || record.source === "transaction" ? "pointer" : "default" },
              })}
              footer={() => (
                <div className="flex justify-between px-4 py-2 bg-gray-50 font-medium">
                  <span>TOTALES</span>
                  <div className="flex gap-8">
                    <span className="text-green-600">
                      {formatCurrency(box.totals?.income || 0)}
                    </span>
                    <span className="text-red-500">
                      {formatCurrency(box.totals?.expense || 0)}
                    </span>
                    <span
                      className={
                        (box.totals?.balance || 0) >= 0
                          ? "text-green-600"
                          : "text-red-500"
                      }
                    >
                      {formatCurrency(box.totals?.balance || 0)}
                    </span>
                  </div>
                </div>
              )}
            />
          </Card>
        ))}

        {!transferLoading.get && filteredBoxes?.length === 0 && (
          <div className="text-center text-gray-500 w-full py-8">
            No hay transferencias para los filtros seleccionados
          </div>
        )}
      </div>

      {/* Debt Modal */}
      <DebtModal
        open={debtModal.open}
        onClose={() => setDebtModal({ open: false, userId: null, type: null, extraInfo: null, transferId: null })}
        userId={debtModal.userId}
        type={debtModal.type}
        extraInfo={debtModal.extraInfo}
        transferId={debtModal.transferId}
        onSuccess={refreshData}
      />

      {/* Passenger Modal */}
      <PassengerModal
        open={passengerModal.open}
        onClose={() => setPassengerModal({ open: false, passengerId: null })}
        passengerId={passengerModal.passengerId}
        onSuccess={refreshData}
      />
    </div>
  );
}

export default Caja;
